/**
 * Two AIO eligibility systems live in this file while the legacy rules form
 * still ships:
 * - LEGACY (features/incident-rules): the hardcoded event-type/criteria sets
 *   (AI_OPERATOR_DOOR_EVENT_TYPES, DFO_CRITERIA, DHO_DOA_CRITERIA) consumed
 *   via getAiOperatorFieldOptions.
 * - V2 (incident-rules-v2): the incident-type-definition-driven helpers keyed
 *   on `aioAllowedCriteria` (getIsAiOperatorSelectable,
 *   getAioCriteriaOptionsForTypes, decorateAioIncidentTypeOptions, ...).
 * New criteria/eligibility behavior belongs in the V2 half; the legacy half
 * is deleted with the legacy feature.
 */
import {
  AiOperatorFieldsQuery,
  DefaultAiOperatorModelQuery,
  DevicesWithTailgateConfiguredQuery,
  EnabledAiOperatorModelsQuery,
  ListIncidentTypesQuery,
} from '@/graphql/generated/operations'
import {
  AiOperatorActionType,
  AiOperatorModel,
  AiOperatorResponseRuleInput,
  RuleEventType,
} from '@/graphql/generated/schemas'
import { OptionI } from '@/utils/forms/optionBuilders'
import { uuid } from '@/utils/helpers'

import {
  AiOperatorModelT,
  Criterion,
  CriterionOption,
  DeviceConfigurationT,
} from '../types/types'

const NO_UNAUTHORIZED_ENTRY = 'No Unauthorized Entry'
const DOOR_LEFT_OPEN = 'Door Left Open'
const PRIOR_ACCESS_GRANTED = 'Prior Access Granted'
const DOOR_CLOSING = 'Door Closing'
const DOOR_FORCED_BOUNCE = 'Door Forced Bounce'
export const DEFAULT_MODEL_ID = 'default'

const DFO_CRITERIA = [
  NO_UNAUTHORIZED_ENTRY,
  PRIOR_ACCESS_GRANTED,
  DOOR_FORCED_BOUNCE,
]
const DHO_DOA_CRITERIA = [DOOR_LEFT_OPEN, DOOR_CLOSING]
const TRACE_LOGS_GATED_CRITERIA = new Set<string>([
  PRIOR_ACCESS_GRANTED,
  DOOR_CLOSING,
  DOOR_FORCED_BOUNCE,
])

export const AI_OPERATOR_DOOR_EVENT_TYPES = new Set<RuleEventType>([
  RuleEventType.DoorForcedOpen,
  RuleEventType.DoorHeldOpen,
  RuleEventType.DoorOpenAlarm,
])

const isAllowedCriterion = (
  criterion: string,
  isTraceLogsEnabled: boolean
): boolean => {
  const isGatedByTraceLogs = TRACE_LOGS_GATED_CRITERIA.has(criterion)
  return !isGatedByTraceLogs || isTraceLogsEnabled
}

/**
 * Get the default action based on the criterion name
 * @param name - Criterion name
 * @returns Default action type
 */
export const getDefaultActionForCriterion = (
  desc: string | null | undefined
): AiOperatorActionType => {
  return desc?.toLocaleLowerCase() === DOOR_LEFT_OPEN.toLocaleLowerCase()
    ? AiOperatorActionType.FlagAndRelease
    : AiOperatorActionType.ResolveAndMarkAsFalse
}

/**
 * Minimal structural shape of a persisted AIO response rule, covering both the
 * legacy Rule query (which selects the full field/model objects) and the
 * narrower RuleV2 selection.
 */
export type AioResponseRuleT = {
  id?: string
  action: AiOperatorActionType
  priority?: number | null
  fieldId?: string
  modelId?: string | null
  field?: { id: string; description?: string | null } | null
  model?: {
    id: string
    canonicalName: string
    provider: AiOperatorModel['provider']
  } | null
}

/**
 * Converts AI Operator fields query data to criterion options for dropdowns
 */
export const getAiOperatorFieldOptions = (
  data: AiOperatorFieldsQuery,
  selectedEventTypes: RuleEventType[],
  isTraceLogsEnabled: boolean
): Criterion[] => {
  const allowedCriteriaDescriptions = new Set<string>()

  if (selectedEventTypes.includes(RuleEventType.DoorForcedOpen)) {
    DFO_CRITERIA.forEach((criterion) => {
      if (isAllowedCriterion(criterion, isTraceLogsEnabled)) {
        allowedCriteriaDescriptions.add(criterion)
      }
    })
  }

  if (
    selectedEventTypes.includes(RuleEventType.DoorHeldOpen) ||
    selectedEventTypes.includes(RuleEventType.DoorOpenAlarm)
  ) {
    DHO_DOA_CRITERIA.forEach((criterion) => {
      if (isAllowedCriterion(criterion, isTraceLogsEnabled)) {
        allowedCriteriaDescriptions.add(criterion)
      }
    })
  }

  const modifiedData: Criterion[] = data?.aiOperatorFields?.edges?.map((f) => ({
    id: f?.node?.id,
    action: getDefaultActionForCriterion(f?.node?.description),
    criterion: {
      id: f?.node?.id,
      description: f?.node?.description,
    },
    detected: true,
  }))
  return (
    modifiedData?.filter((criterion) =>
      allowedCriteriaDescriptions.has(criterion?.criterion?.description)
    ) ?? []
  )
}

/**
 * Returns the `aioAllowedCriteria` list of each selected incident type
 * definition. A selected id with no matching definition yields an empty list,
 * which reads as "not AIO-eligible".
 */
const getAllowedCriteriaLists = (
  incidentTypesData: ListIncidentTypesQuery | undefined,
  selectedTypeIds: string[]
): string[][] => {
  const itemsById = new Map(
    (incidentTypesData?.incidentTypes?.items ?? []).map((item) => [
      item.id,
      item,
    ])
  )
  return selectedTypeIds.map(
    (id) => itemsById.get(id)?.aioAllowedCriteria ?? []
  )
}

// Criteria ids are UUIDs, so '|' can never appear inside an id.
const toCriteriaListKey = (list: string[]): string => [...list].sort().join('|')

/**
 * Display names of the incident type definitions that are AIO-eligible
 * (non-archived, non-empty `aioAllowedCriteria`), sorted alphabetically.
 * Drives the dynamic "Only compatible with: ..." helper text so it stays
 * correct as eligibility data changes.
 */
export const getAioEligibleTypeNames = (
  incidentTypesData: ListIncidentTypesQuery | undefined
): string[] =>
  (incidentTypesData?.incidentTypes?.items ?? [])
    .filter(
      (item) => !item.archived && (item.aioAllowedCriteria ?? []).length > 0
    )
    .map((item) => item.displayName)
    .sort((a, b) => a.localeCompare(b))

/**
 * The compatibility-group key of an incident type definition: its sorted,
 * joined `aioAllowedCriteria` list. Empty string = not AIO-eligible. Two types
 * are co-selectable under AI Operator exactly when their non-empty keys are
 * equal (same rule as getIsAiOperatorSelectable).
 */
export const getAioTypeCompatibilityKey = (
  incidentTypesData: ListIncidentTypesQuery | undefined,
  typeId: string
): string => {
  const [list] = getAllowedCriteriaLists(incidentTypesData, [typeId])
  return toCriteriaListKey(list ?? [])
}

interface DecorateAioIncidentTypeOptionsIProps {
  baseOptions: OptionI[]
  incidentTypesData: ListIncidentTypesQuery | undefined
  selectedTypeIds: string[]
  isAioActionSelected: boolean
  /** Localized "Compatible with AI Operator" indicator text. */
  compatibleLabel: string
}

/**
 * Decorates ITD select options for the AIO create path: AIO-eligible types get
 * the inline compatibility indicator as a subtitle, and with Enable AI
 * Operator selected, types outside the already-selected compatibility group
 * (or ineligible outright) are disabled — the legacy mutual-exclusion rule,
 * data-driven. The compatibility-key index is built once per call rather than
 * per option.
 */
export const decorateAioIncidentTypeOptions = ({
  baseOptions,
  incidentTypesData,
  selectedTypeIds,
  isAioActionSelected,
  compatibleLabel,
}: DecorateAioIncidentTypeOptionsIProps): OptionI[] => {
  const keysByTypeId = new Map(
    (incidentTypesData?.incidentTypes?.items ?? []).map((item) => [
      item.id,
      toCriteriaListKey(item.aioAllowedCriteria ?? []),
    ])
  )

  // Co-selection group of the current selection; '' when nothing is selected
  // (a valid AIO selection always shares one key, so the first id suffices).
  const selectedKey = selectedTypeIds.length
    ? keysByTypeId.get(selectedTypeIds[0] ?? '') ?? ''
    : ''

  return baseOptions.map((option) => {
    const key = keysByTypeId.get(option.value) ?? ''
    const isAioEligible = key !== ''
    const isOutsideSelectedGroup =
      isAioActionSelected &&
      (!isAioEligible || (selectedKey !== '' && key !== selectedKey))
    return {
      ...option,
      ...(isAioEligible && { subtitle: compatibleLabel }),
      ...(isOutsideSelectedGroup && { isDisabled: true }),
    }
  })
}

/**
 * Reconciles the working criteria list with the available options: criteria
 * whose option disappeared are pruned, every option not currently present is
 * added as a default (all criteria are included by default, as in legacy),
 * and the user's ordering/action/model choices on surviving criteria are
 * preserved. Pure so callers can key WHEN it runs — re-running it on a mere
 * options-array identity change would resurrect user-deleted criteria.
 */
export const syncCriteriaToOptions = (
  previousCriteria: Criterion[],
  criteriaOptions: Criterion[]
): Criterion[] => {
  if (criteriaOptions.length === 0) {
    return []
  }

  const allowedCriterionIds = new Set(
    criteriaOptions.map((criterion) => criterion.criterion.id)
  )

  const filteredPrevious = previousCriteria.filter((criterion) =>
    allowedCriterionIds.has(criterion.criterion.id)
  )

  const selectedCriterionIds = new Set(
    filteredPrevious.map((criterion) => criterion.criterion.id)
  )

  const missingCriteria = criteriaOptions
    .filter((criterion) => !selectedCriterionIds.has(criterion.criterion.id))
    .map((criterion) => ({
      id: uuid(),
      action: criterion.action,
      criterion: criterion.criterion,
      detected: criterion.detected,
      // undefined = use current default model (dynamic, not pinned)
      aiOperatorModelId: undefined,
    }))

  return [...filteredPrevious, ...missingCriteria]
}

/**
 * Whether AI Operator can be selected for the given incident type selection.
 * True only when at least one type is selected, every selected type is
 * AIO-eligible (non-empty `aioAllowedCriteria`), and all selected types share
 * the same allowed-criteria list. Two types are in the same compatibility
 * group exactly when their lists are identical, so this reproduces the legacy
 * mutual-exclusion rule without hardcoding event types.
 */
export const getIsAiOperatorSelectable = (
  incidentTypesData: ListIncidentTypesQuery | undefined,
  selectedTypeIds: string[]
): boolean => {
  const [first, ...rest] = getAllowedCriteriaLists(
    incidentTypesData,
    selectedTypeIds
  )
  if (!first || first.length === 0) return false
  if (rest.some((list) => list.length === 0)) return false
  const firstKey = toCriteriaListKey(first)
  return rest.every((list) => toCriteriaListKey(list) === firstKey)
}

/**
 * Converts AI Operator fields query data to criterion options for the selected
 * incident type definitions, driven by their `aioAllowedCriteria` lists.
 * Co-selection is only possible within a compatibility group (identical
 * lists, see getIsAiOperatorSelectable), so the first selected type's list is
 * the shared allowed list. Trace-logs-gated criteria are still filtered out in
 * the frontend on top of the database-driven lists.
 */
export const getAioCriteriaOptionsForTypes = (
  data: AiOperatorFieldsQuery,
  incidentTypesData: ListIncidentTypesQuery | undefined,
  selectedTypeIds: string[],
  isTraceLogsEnabled: boolean
): Criterion[] => {
  const [sharedList] = getAllowedCriteriaLists(
    incidentTypesData,
    selectedTypeIds
  )
  const allowedCriteriaIds = new Set(sharedList ?? [])

  const modifiedData: Criterion[] = data?.aiOperatorFields?.edges?.map((f) => ({
    id: f?.node?.id,
    action: getDefaultActionForCriterion(f?.node?.description),
    criterion: {
      id: f?.node?.id,
      description: f?.node?.description,
    },
    detected: true,
  }))
  return (
    modifiedData?.filter(
      (criterion) =>
        allowedCriteriaIds.has(criterion?.criterion?.id) &&
        isAllowedCriterion(
          criterion?.criterion?.description,
          isTraceLogsEnabled
        )
    ) ?? []
  )
}

/**
 * Returns all devices which are missing device configurations. Either devices are not associated or missing a tailgate grid
 *
 * @param devicesData - Device DevicesWithTailgateConfiguredQuery data
 * @returns All misconfiguared door devices
 */
export const getDeviceConfigurations = (
  devicesData: DevicesWithTailgateConfiguredQuery
): DeviceConfigurationT[] => {
  return (
    devicesData?.devices?.edges?.flatMap((edge) => {
      const door = edge?.node?.door
      const isAssociated = door?.tailgateGrids?.length > 0
      const isConfigured = door?.tailgateGrids?.some(
        (grid) => grid?.inBox?.topLeft
      )
      if (!isAssociated || !isConfigured) {
        return {
          id: edge.node.id,
          name: edge.node.name,
          floorId: edge.node.floor?.id,
          isAssociated,
          isConfigured,
        }
      }
      return []
    }) || []
  )
}

export const containsNoUnauthorizedEntry = (criteria: Criterion[]): boolean => {
  return criteria?.some(
    (crit) =>
      crit.criterion?.description?.toLowerCase() ===
      NO_UNAUTHORIZED_ENTRY.toLocaleLowerCase()
  )
}
/**
 * Returns all enabled AI operator models from the query data
 *
 * @param aiOperatorModelsData - EnabledAiOperatorModelsQuery data
 * @returns Array of AI operator models
 */
export const getAiOperatorModels = (
  aiOperatorModelsData?: EnabledAiOperatorModelsQuery
): AiOperatorModelT[] => {
  return (
    aiOperatorModelsData?.enabledAiOperatorModels?.edges
      ?.map((edge) => edge?.node)
      ?.filter(Boolean)
      ?.map((model) => ({
        id: model.id,
        canonicalName: model.canonicalName,
        provider: model.provider,
      })) || []
  )
}

/**
 * Returns the default AI operator model from the query data
 *
 * @param defaultAiOperatorModelData - DefaultAiOperatorModelQuery data
 * @returns Default AI operator model or null
 */
export const getDefaultAiOperatorModel = (
  defaultAiOperatorModelData?: DefaultAiOperatorModelQuery
): AiOperatorModelT | null => {
  const model =
    defaultAiOperatorModelData?.defaultAiOperatorModel?.aiOperatorModel
  if (!model) return null

  return {
    id: model.id,
    canonicalName: model.canonicalName,
    provider: model.provider,
  }
}

const defaultModel = {
  id: DEFAULT_MODEL_ID,
  canonicalName: 'Default',
  provider: 'DEFAULT',
}

/**
 * Gets the models to show in a criterion's dropdown based on the viewing mode
 *
 * @param criterion - The criterion object containing model information
 * @param allModels - All available AI operator models (from query)
 * @param isViewingMode - Whether we're viewing an existing rule (disabled mode)
 * @returns Array of models to display in the dropdown
 *
 * Logic:
 * - Creation mode: Show "Default" option plus all available models
 * - Viewing mode + explicit model: Show only that specific model
 * - Viewing mode + no explicit model: Show "Default" label
 */
export const getModelsForCriterionDropdown = (
  criterion: {
    aiOperatorModelId?: string
    aiOperatorModel?: AiOperatorModelT
  },
  allModels: AiOperatorModelT[],
  isViewingMode = false
): Array<{ id: string; canonicalName: string; provider: string }> => {
  // Creation mode: show "Default" option plus all available models
  if (!isViewingMode) {
    return [defaultModel, ...allModels]
  }

  if (criterion.aiOperatorModel) {
    return [criterion.aiOperatorModel]
  }

  // No explicit model = using default, show "Default" label
  if (!criterion.aiOperatorModelId) {
    return [defaultModel]
  }

  return []
}

/**
 * Calculates available criteria options for a specific item, excluding options
 * already used by other criteria items. This prevents duplicate selections
 * in the dropdowns.
 *
 * @param currentItem - The criterion item we're calculating options for
 * @param allCriteria - All criteria currently in the list
 * @param availableCriteria - All available criteria from the API
 * @returns Filtered array of criteria options available for this specific item
 */
export const getAvailableCriteriaForItem = (
  currentItem: Criterion,
  allCriteria: Criterion[],
  availableCriteria: Criterion[]
): CriterionOption[] => {
  const usedCriteriaValues = allCriteria
    .filter((c) => c.id !== currentItem.id)
    .map((c) => c.criterion.id)

  const options = availableCriteria
    .map((c) => c.criterion)
    .filter((option) => !usedCriteriaValues.includes(option.id))

  // A persisted criterion can fall out of the eligible options after creation
  // (trace-logs gating flipped, field disabled, incident type's allowed list
  // changed). The row's native select renders blank without a matching option,
  // so the item's own criterion is always included.
  const hasOwnCriterion = options.some(
    (option) => option.id === currentItem.criterion.id
  )
  return hasOwnCriterion ? options : [currentItem.criterion, ...options]
}

/**
 * Converts GraphQL AiOperatorField to CriterionOption
 */
export const convertFieldToCriterionOption = (
  field: NonNullable<AioResponseRuleT['field']>
): CriterionOption => ({
  id: field.id,
  description: field.description ?? '',
})

/**
 * Converts GraphQL AiOperatorModel to AiOperatorModelT
 * Accepts any object with id, canonicalName, and provider fields
 */
export const convertModelToModelT = (model: {
  id: string
  canonicalName: string
  provider: AiOperatorModel['provider']
}): AiOperatorModelT => ({
  id: model.id,
  canonicalName: model.canonicalName,
  provider: model.provider,
})

/**
 * Converts GraphQL AiOperatorResponseRule to Criterion with proper type safety
 */
export const convertResponseRuleToCriterion = (
  rule: AioResponseRuleT & { field: NonNullable<AioResponseRuleT['field']> }
): Criterion => {
  return {
    id: uuid(),
    action: rule.action,
    criterion: convertFieldToCriterionOption(rule.field),
    detected: true,
    aiOperatorModelId: rule.model?.id,
    aiOperatorModel: rule.model ? convertModelToModelT(rule.model) : undefined,
  }
}

/**
 * Sorts response rules by priority (highest first) with null safety
 */
export const sortResponseRulesByPriority = (
  rules: AioResponseRuleT[]
): AioResponseRuleT[] => {
  return [...rules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
}

/**
 * Converts array of GraphQL AiOperatorResponseRule to Criterion array for preview/view mode
 */
export const convertResponseRulesToPreviewData = (
  responseRules: AioResponseRuleT[]
): Criterion[] => {
  if (responseRules.length === 0) {
    return []
  }

  const sortedRules = sortResponseRulesByPriority(responseRules)
  // Rules whose field reference failed to resolve can't be rendered or
  // re-saved meaningfully, so they are dropped. They must still survive a
  // save (the update mutation replaces the full rule set) — see
  // getUnresolvedResponseRuleInputs.
  return sortedRules.flatMap((rule) =>
    rule.field
      ? [convertResponseRuleToCriterion({ ...rule, field: rule.field })]
      : []
  )
}

/**
 * Persisted response rules whose field reference failed to resolve are not
 * rendered (see convertResponseRulesToPreviewData), but the update mutation
 * replaces the rule's full response-rule set, so they must be carried back
 * into the save payload or any save (even a dry-run-only toggle) silently
 * deletes them.
 */
export const getUnresolvedResponseRuleInputs = (
  responseRules: AioResponseRuleT[]
): AiOperatorResponseRuleInput[] =>
  responseRules.flatMap((rule) =>
    !rule.field && rule.fieldId
      ? [
          {
            action: rule.action,
            detected: true,
            fieldId: rule.fieldId,
            ...(rule.modelId && { modelId: rule.modelId }),
          },
        ]
      : []
  )
